export const APP_PERFORMANCE_MARKS = {
  clientEntryStarted: 'tipsy:app:client_entry_started',
  reactHydrationCommitted: 'tipsy:app:react_hydration_committed',
} as const

export const HOME_PERFORMANCE_MARKS = {
  ...APP_PERFORMANCE_MARKS,
  homeMounted: 'tipsy:home:mounted',
  pageSizeMeasured: 'tipsy:home:page_size_measured',
  filtersHydrated: 'tipsy:home:filters_hydrated',
  listRequestStart: 'tipsy:home:list_request_start',
  listRequestEnd: 'tipsy:home:list_request_end',
  dataReady: 'tipsy:home:data_ready',
  gridCommitted: 'tipsy:home:grid_committed',
  targetLocked: 'tipsy:home:target_locked',
  allMediaReady: 'tipsy:home:all_media_ready',
  postMediaPaint: 'tipsy:home:post_media_paint',
} as const

export const CUSTOM_CHAT_PERFORMANCE_MARKS = {
  characterReady: 'tipsy:custom_chat:character_ready',
  historyRequestStart: 'tipsy:custom_chat:history_request_start',
  historyRequestEnd: 'tipsy:custom_chat:history_request_end',
  messageListReady: 'tipsy:custom_chat:message_list_ready',
  targetLocked: 'tipsy:custom_chat:target_locked',
  firstMessageReady: 'tipsy:custom_chat:first_message_ready',
  allResourcesReady: 'tipsy:custom_chat:all_resources_ready',
} as const

export const CHAT_PERFORMANCE_MARKS = {
  ...APP_PERFORMANCE_MARKS,
  chatPageMounted: 'tipsy:chat:page_mounted',
  characterReady: 'tipsy:chat:character_ready',
  historyRequestStart: 'tipsy:chat:history_request_start',
  historyRequestEnd: 'tipsy:chat:history_request_end',
  messageListReady: 'tipsy:chat:message_list_ready',
  targetLocked: 'tipsy:chat:target_locked',
  firstMessageReady: 'tipsy:chat:first_message_ready',
  allResourcesReady: 'tipsy:chat:all_resources_ready',
  postResourcesPaint: 'tipsy:chat:post_resources_paint',
} as const

type PerformanceMarks = Record<string, string>

const roundMs = (value: number) => Math.round(Math.max(0, value) * 100) / 100
const ownedMarkNames = [
  ...Object.values(APP_PERFORMANCE_MARKS),
  ...Object.values(HOME_PERFORMANCE_MARKS),
  ...Object.values(CHAT_PERFORMANCE_MARKS),
  ...Object.values(CUSTOM_CHAT_PERFORMANCE_MARKS),
]
let activePathname: string | undefined

export const markPerformanceStage = (name: string) => {
  try {
    const pathname = window.location.pathname
    if (activePathname !== pathname) {
      ownedMarkNames.forEach((markName) => performance.clearMarks(markName))
      activePathname = pathname
    }
    performance.mark(name)
  } catch {
    // Performance telemetry must never affect page rendering.
  }
}

export const runWithPerformanceStages = async <T>(
  startMark: string,
  endMark: string,
  operation: () => Promise<T>
): Promise<T> => {
  markPerformanceStage(startMark)
  try {
    return await operation()
  } finally {
    markPerformanceStage(endMark)
  }
}

type FirstScreenReadyRecord = {
  readyAtSinceRouteMs: number
  sequence: number
}

/**
 * 记录媒体 ready 细分时序。任何采集异常都降级为空记录，不能阻断业务 ready 状态。
 */
export const recordFirstScreenReady = <Key>(
  records: Map<Key, FirstScreenReadyRecord>,
  key: Key,
  sequence: number,
  getRouteDurationMs: () => number | undefined
): number => {
  try {
    if (records.has(key)) return sequence

    const readyAtSinceRouteMs = getRouteDurationMs()
    if (
      typeof readyAtSinceRouteMs !== 'number' ||
      !Number.isFinite(readyAtSinceRouteMs)
    ) {
      return sequence
    }

    const nextSequence = sequence + 1
    records.set(key, {
      readyAtSinceRouteMs,
      sequence: nextSequence,
    })
    return nextSequence
  } catch {
    return sequence
  }
}

export const collectPerformanceStages = (
  marks: PerformanceMarks,
  routeDurationMs?: number
) => {
  if (typeof routeDurationMs !== 'number') return {}

  try {
    const routeStartedAt = performance.now() - routeDurationMs
    return Object.fromEntries(
      Object.entries(marks).flatMap(([stage, markName]) => {
        const entry = performance
          .getEntriesByName(markName, 'mark')
          .find((item) => item.startTime >= routeStartedAt)
        const field = stage.replace(
          /[A-Z]/g,
          (char) => `_${char.toLowerCase()}`
        )
        return entry
          ? [
              [
                `${field}_since_route_ms`,
                roundMs(entry.startTime - routeStartedAt),
              ],
            ]
          : []
      })
    )
  } catch {
    return {}
  }
}

export const clearPerformanceStages = (marks: PerformanceMarks) => {
  try {
    Object.values(marks).forEach((name) => performance.clearMarks(name))
  } catch {
    // Performance telemetry must never affect page rendering.
  }
}
